/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Injectable } from '@angular/core';
import { Observable, Subject, BehaviorSubject } from 'rxjs';
import { debounceTime, map, takeUntil } from 'rxjs/operators';
import {
	mapValuesToArray,
	simpleSortArray,
	subjectSubscribeWithoutComplete,
} from 'src/app/plain-javascript/services/rxjs-internal-extensions';

import { sortPlansByName } from 'src/app/utils/array-util';

import { Plan } from '@touchplan/common';
import { FancyFirebasePlansApiBuilder } from 'src/app/services/fancy-firebase-api-builder/fancy-firebase-plans-api-builder.service';
import { ProjectLegacy } from 'src/app/app-data/models/Project';
import { FancyFirebaseProjectsApiBuilder } from 'src/app/services/fancy-firebase-api-builder/fancy-firebase-projects-api-builder.service';

@Injectable({
	providedIn: 'root',
})
export class PlanService {
	public plansList$: Observable<Plan[]>;
	private shutdownSubject$: Subject<boolean> = new Subject();
	public project$ = new BehaviorSubject<ProjectLegacy>(null);
	public allowedProjectLinks$ = new BehaviorSubject<string[]>(null);
	private projectObject;

	constructor(
		private fancyFirebaseProjectsApiBuilder: FancyFirebaseProjectsApiBuilder,
		private fancyFirebasePlansApiBuilder: FancyFirebasePlansApiBuilder
	) {}

	public getPlanList(projectId: string) {
		const planApi = this.fancyFirebasePlansApiBuilder.getPlanApi(projectId);
		this.plansList$ = planApi.data.list$.pipe(
			debounceTime(10),
			mapValuesToArray(),
			simpleSortArray(sortPlansByName),
			takeUntil(this.shutdownSubject$)
		);
		this.subscribeToProjectObject(projectId);

		return this.plansList$;
	}
	private subscribeToProjectObject(projectId: string) {
		this.projectObject = this.fancyFirebaseProjectsApiBuilder.getProjectObject(projectId);
		this.projectObject.object$
			.pipe(takeUntil(this.shutdownSubject$))
			.subscribe(subjectSubscribeWithoutComplete(this.project$));
		this.fetchAllowedLinks(projectId);
		// TODO: Following TP-3262 to follow an improved pattern later
	}

	// TEMP STUFF: all of functions bellow should be migrated and likely to be rewritten to not write to firebase directly when ProjectLegacy is handled
	public fetchAllowedLinks(projectId) {
		this.project$
			.pipe(
				takeUntil(this.shutdownSubject$),
				map((project) => {
					if (project && project.allowedLinks) {
						return project.allowedLinks;
					} else {
						console.log('Allowed links list does not exist.');
						return null;
					}
				})
			)
			.subscribe(this.allowedProjectLinks$);
	}

	public destroy() {
		this.shutdownSubject$.next(true);
		this.projectObject.destroy();
		this.projectObject = null;
	}
}
